import { Link, NavLink, useLocation } from 'react-router';
import CardIcon from '@/assets/icons/card.svg?react';
import HomeIcon from '@/assets/icons/home.svg?react';
import PlugIcon from '@/assets/icons/plug.svg?react';
import SignOutIcon from '@/assets/icons/sign-out.svg?react';
import { ROUTES } from '@/shared/config/routes';
import { cn } from '@/shared/lib/cn';
import { summarizeUsage } from '@/shared/lib/pod-usage';
import type { ViewerUser, WorkspaceData } from '@/shared/types/workspace';
import { Avatar } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Meter } from '@/shared/ui/meter';
import { Wordmark } from '@/shared/ui/wordmark';
import { useSignOut } from '../hooks/use-sign-out';
import { planLabel, workspaceInitial } from '../model/plan-label';

type AppSidebarProps = { user: ViewerUser; workspace: WorkspaceData };

const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
  cn(
    'flex h-9 items-center gap-2.5 whitespace-nowrap rounded-nav px-2.5 font-medium text-muted transition-tone hover:bg-surface-2 hover:text-ink',
    isActive && 'bg-surface-2 text-ink',
  );

export function AppSidebar({ user, workspace }: AppSidebarProps) {
  const { pathname } = useLocation();
  const { signOut, isSigningOut } = useSignOut();
  const usage = summarizeUsage(workspace.usage);
  const isFirstRun = pathname === ROUTES.GET_STARTED;

  return (
    <aside className="flex shrink grow basis-60 flex-col gap-3 bg-side px-4 py-3 shell:gap-5 shell:px-3.5 shell:py-5">
      <div className="flex items-center justify-between">
        <Link to={ROUTES.OVERVIEW} className="px-1.5 pt-1">
          <Wordmark />
        </Link>
        {/* Phone width hides the account block below, so sign-out stays reachable here. */}
        <Button
          variant="ghost"
          size="icon"
          className="shell:hidden"
          aria-label="Sign out"
          isLoading={isSigningOut}
          onClick={() => void signOut()}
        >
          {isSigningOut ? null : <SignOutIcon className="size-4" />}
        </Button>
      </div>
      <div className="flex items-center gap-2.5 rounded-control-lg bg-surface p-2.5">
        <Avatar initials={workspaceInitial(workspace.name)} isWorkspace />
        <span className="flex min-w-0 flex-col leading-4">
          <span className="truncate font-semibold">{workspace.name}</span>
          <span className="text-meta text-muted">{planLabel(workspace.plan)}</span>
        </span>
      </div>
      <nav aria-label="Main" className="flex flex-row gap-0.5 overflow-x-auto shell:flex-col">
        {isFirstRun ? (
          <NavLink to={ROUTES.GET_STARTED} className={navLinkClass}>
            <HomeIcon className="size-4 shrink-0" />
            Get started
          </NavLink>
        ) : (
          <NavLink to={ROUTES.OVERVIEW} end className={navLinkClass}>
            <HomeIcon className="size-4 shrink-0" />
            Overview
          </NavLink>
        )}
        <NavLink to={ROUTES.BILLING} className={navLinkClass}>
          <CardIcon className="size-4 shrink-0" />
          Billing
        </NavLink>
        <NavLink to={ROUTES.CONNECTIONS} className={navLinkClass}>
          <PlugIcon className="size-4 shrink-0" />
          Connections
        </NavLink>
      </nav>
      <div className="mt-auto hidden flex-col gap-4 shell:flex">
        <Card asChild isInteractive>
          <Link to={ROUTES.BILLING} className="flex flex-col gap-2 p-3.5">
            <span className="text-meta text-muted">{usage.label}</span>
            <span className="font-semibold tabular-nums">
              {usage.used} <span className="font-normal text-muted">of {usage.limit}</span>
            </span>
            <Meter ratio={usage.ratio} tone={usage.meterTone} />
          </Link>
        </Card>
        <div className="flex items-center gap-2.5 px-1">
          <Avatar initials={user.initials} />
          <span className="flex min-w-0 flex-col leading-4">
            <span className="truncate font-medium">{user.name ?? user.email.split('@')[0]}</span>
            <span className="truncate text-meta text-muted">{user.email}</span>
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto"
            aria-label="Sign out"
            isLoading={isSigningOut}
            onClick={() => void signOut()}
          >
            {isSigningOut ? null : <SignOutIcon className="size-4" />}
          </Button>
        </div>
      </div>
    </aside>
  );
}
